/**
 * FireAlarmHero — SVG illustration of an NFPA 72 compliant fire alarm system.
 *
 * Replaces the AI-generated hero photo (which had "SIGNEL" misspelled on the
 * pull station). This SVG is brand-aligned, vector-crisp at any size, and uses
 * the correct real-world labeling: "PULL DOWN" / "FIRE" on the station and
 * proper FACP LED labels.
 */
export function FireAlarmHero({ className = "" }) {
  return (
    <svg
      viewBox="0 0 480 600"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Fire alarm control panel and manual pull station"
      className={className}
    >
      {/* Wall background */}
      <defs>
        <linearGradient id="wall" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f4f4f5" />
          <stop offset="100%" stopColor="#e4e4e7" />
        </linearGradient>
        <linearGradient id="panel" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fafafa" />
          <stop offset="100%" stopColor="#e7e7e9" />
        </linearGradient>
        <linearGradient id="pullStation" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#dc2626" />
          <stop offset="100%" stopColor="#b91c1c" />
        </linearGradient>
        <filter id="softShadow">
          <feDropShadow dx="0" dy="3" stdDeviation="4" floodOpacity="0.18" />
        </filter>
        <filter id="glow">
          <feGaussianBlur stdDeviation="2" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <rect x="0" y="0" width="480" height="600" fill="url(#wall)" />

      {/* Subtle grid pattern — feels like a tech blueprint */}
      <g stroke="#d4d4d8" strokeWidth="0.5" opacity="0.4">
        {[...Array(12)].map((_, i) => (
          <line key={`v${i}`} x1={i * 40} y1="0" x2={i * 40} y2="600" />
        ))}
        {[...Array(15)].map((_, i) => (
          <line key={`h${i}`} x1="0" y1={i * 40} x2="480" y2={i * 40} />
        ))}
      </g>

      {/* ─── FIRE ALARM CONTROL PANEL (FACP) ─── */}
      <g filter="url(#softShadow)" transform="translate(60, 60)">
        {/* Outer enclosure */}
        <rect x="0" y="0" width="360" height="280" fill="url(#panel)" stroke="#52525b" strokeWidth="2" rx="3" />
        {/* Header strip */}
        <rect x="0" y="0" width="360" height="34" fill="#18181b" rx="3" />
        <rect x="0" y="30" width="360" height="4" fill="#18181b" />
        <text x="20" y="22" fill="#ffffff" fontFamily="ui-monospace, monospace" fontSize="11" fontWeight="700" letterSpacing="2">
          FIRE ALARM CONTROL PANEL
        </text>
        <text x="340" y="22" fill="#a1a1aa" fontFamily="ui-monospace, monospace" fontSize="9" textAnchor="end" letterSpacing="1">
          NFPA 72
        </text>

        {/* LCD screen */}
        <rect x="20" y="50" width="200" height="60" fill="#0a0a0a" rx="2" />
        <rect x="22" y="52" width="196" height="56" fill="#052e16" rx="1" />
        <text x="32" y="72" fill="#86efac" fontFamily="ui-monospace, monospace" fontSize="10" fontWeight="700">
          SYSTEM NORMAL
        </text>
        <text x="32" y="88" fill="#86efac" fontFamily="ui-monospace, monospace" fontSize="9">
          ALL DEVICES OK
        </text>
        <text x="32" y="102" fill="#65a30d" fontFamily="ui-monospace, monospace" fontSize="8">
          2026-02-15  09:42
        </text>

        {/* Status LEDs */}
        {[
          { y: 130, color: "#16a34a", label: "AC POWER", on: true },
          { y: 152, color: "#16a34a", label: "BATTERY OK", on: true },
          { y: 174, color: "#dc2626", label: "ALARM", on: false },
          { y: 196, color: "#eab308", label: "TROUBLE", on: false },
          { y: 218, color: "#eab308", label: "SUPERVISORY", on: false },
          { y: 240, color: "#dc2626", label: "SILENCED", on: false },
        ].map((l, i) => (
          <g key={i}>
            <circle
              cx="32"
              cy={l.y}
              r="5"
              fill={l.on ? l.color : "#27272a"}
              filter={l.on ? "url(#glow)" : undefined}
              opacity={l.on ? 1 : 0.55}
            />
            <text x="48" y={l.y + 4} fill="#27272a" fontFamily="ui-monospace, monospace" fontSize="9" fontWeight="600" letterSpacing="0.5">
              {l.label}
            </text>
          </g>
        ))}

        {/* Right side: keypad */}
        <g transform="translate(240, 50)">
          {[0, 1, 2, 3].map((row) =>
            [0, 1, 2].map((col) => {
              const labels = [
                ["ACK", "RESET", "SILENCE"],
                ["DRILL", "TEST", "TROUBLE"],
                ["1", "2", "3"],
                ["4", "5", "6"],
              ];
              return (
                <g key={`${row}-${col}`}>
                  <rect
                    x={col * 38}
                    y={row * 42}
                    width="32"
                    height="34"
                    fill="#fafafa"
                    stroke="#a1a1aa"
                    strokeWidth="0.8"
                    rx="2"
                  />
                  <text
                    x={col * 38 + 16}
                    y={row * 42 + 21}
                    fill="#27272a"
                    fontFamily="ui-monospace, monospace"
                    fontSize={labels[row][col].length > 3 ? "7" : "9"}
                    fontWeight="700"
                    textAnchor="middle"
                  >
                    {labels[row][col]}
                  </text>
                </g>
              );
            })
          )}
        </g>
      </g>

      {/* ─── MANUAL PULL STATION (the corrected element) ─── */}
      <g filter="url(#softShadow)" transform="translate(120, 380)">
        {/* Mounting backplate */}
        <rect x="-8" y="-8" width="156" height="186" fill="#fef2f2" stroke="#fca5a5" strokeWidth="1" />

        {/* Pull station body */}
        <rect x="0" y="0" width="140" height="170" fill="url(#pullStation)" stroke="#7f1d1d" strokeWidth="2" rx="3" />

        {/* Top label band */}
        <text x="70" y="22" fill="#ffffff" fontFamily="ui-monospace, monospace" fontSize="9" fontWeight="700" textAnchor="middle" letterSpacing="3">
          PULL DOWN
        </text>
        <line x1="14" y1="29" x2="126" y2="29" stroke="#ffffff" strokeWidth="0.6" opacity="0.5" />

        {/* Big FIRE text (the corrected word) */}
        <text x="70" y="78" fill="#ffffff" fontFamily="ui-sans-serif, system-ui, sans-serif" fontSize="38" fontWeight="900" textAnchor="middle" letterSpacing="2">
          FIRE
        </text>

        {/* Bottom label band */}
        <line x1="14" y1="98" x2="126" y2="98" stroke="#ffffff" strokeWidth="0.6" opacity="0.5" />
        <text x="70" y="114" fill="#ffffff" fontFamily="ui-monospace, monospace" fontSize="7" fontWeight="700" textAnchor="middle" letterSpacing="2">
          IN CASE OF
        </text>

        {/* Pull handle (T-shaped lever) */}
        <rect x="46" y="124" width="48" height="14" fill="#ffffff" stroke="#7f1d1d" strokeWidth="1" rx="2" />
        <rect x="64" y="138" width="12" height="22" fill="#ffffff" stroke="#7f1d1d" strokeWidth="1" />
        <text x="70" y="135" fill="#dc2626" fontFamily="ui-monospace, monospace" fontSize="7" fontWeight="700" textAnchor="middle" letterSpacing="1">
          PULL
        </text>

        {/* Arrow pointing down on the handle */}
        <path d="M 70 145 L 70 155 M 66 151 L 70 155 L 74 151" stroke="#dc2626" strokeWidth="1.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </g>

      {/* ─── HORN/STROBE NOTIFICATION APPLIANCE (extra detail to fill the visual) ─── */}
      <g filter="url(#softShadow)" transform="translate(330, 410)">
        {/* Backplate */}
        <rect x="0" y="0" width="100" height="130" fill="#ffffff" stroke="#52525b" strokeWidth="1.5" rx="2" />
        {/* "FIRE" label band */}
        <rect x="0" y="0" width="100" height="22" fill="#dc2626" />
        <text x="50" y="15" fill="#ffffff" fontFamily="ui-sans-serif, system-ui, sans-serif" fontSize="11" fontWeight="900" textAnchor="middle" letterSpacing="3">
          FIRE
        </text>
        {/* Strobe lens */}
        <rect x="10" y="32" width="80" height="58" fill="#fef9c3" stroke="#a16207" strokeWidth="1" rx="2" opacity="0.92" />
        <text x="50" y="66" fill="#a16207" fontFamily="ui-monospace, monospace" fontSize="20" fontWeight="900" textAnchor="middle" opacity="0.8">
          ⚡
        </text>
        {/* Horn grille */}
        <g stroke="#52525b" strokeWidth="0.8" fill="none">
          {[0, 1, 2, 3].map((i) => (
            <line key={i} x1={20} y1={100 + i * 5} x2={80} y2={100 + i * 5} />
          ))}
        </g>
        <text x="50" y="124" fill="#52525b" fontFamily="ui-monospace, monospace" fontSize="6" fontWeight="700" textAnchor="middle" letterSpacing="1">
          15CD · 75CD
        </text>
      </g>
    </svg>
  );
}
